import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { leadVoisin } from "./etatPanneau";

// Panneau de détail d'un lead, à droite de la liste : en-tête (produit, nom,
// navigation entre leads, fermeture), ligne d'état (`etat` : statut,
// assignation, avancement…), onglets de la page, puis `contenu(onglet)`. Rendu
// par la carte du lead ouvert, dans un portail, pour garder son contexte
// (données, actions, gestionnaires). Échap ferme, Alt + flèche haut ou bas
// passe au lead voisin ; à la fermeture, le focus revient à la barre d'accès
// de la carte (`idCarte`).
const PanneauLead = ({ idCarte, leadId, panneau, ton = "primary", produit, titre, sousTitre, etat, extra = {}, contenu }) => {
  const refTitre = useRef(null);
  const derniers = useRef({});
  const { onglets, onglet } = panneau;
  derniers.current = { idCarte, onglet };

  const precedent = leadVoisin(panneau.ids, leadId, -1);
  const suivant = leadVoisin(panneau.ids, leadId, 1);

  useEffect(() => {
    // Le panneau est fixe : rien à faire défiler pour lui donner le focus.
    if (refTitre.current) refTitre.current.focus({ preventScroll: true });
  }, [leadId]);

  useEffect(() => {
    const surClavier = (evenement) => {
      if (evenement.key === "Escape") {
        if (evenement.defaultPrevented) return;
        // Un champ en cours de saisie ou un menu ouvert garde Échap pour lui.
        if (evenement.target.closest && evenement.target.closest(".ck-editor, input, textarea, select, [role='menu']")) return;
        panneau.fermer();
      } else if (evenement.altKey && (evenement.key === "ArrowUp" || evenement.key === "ArrowDown")) {
        evenement.preventDefault();
        panneau.naviguer(evenement.key === "ArrowUp" ? -1 : 1);
      }
    };
    document.addEventListener("keydown", surClavier);
    return () => document.removeEventListener("keydown", surClavier);
  }, [panneau]);

  // Fermeture : le focus revient au bouton de la barre d'accès de la carte
  // (sauf si c'est le panneau d'un autre lead qui prend la place).
  useEffect(
    () => () => {
      const { idCarte: derniereCarte, onglet: dernierOnglet } = derniers.current;
      setTimeout(() => {
        if (document.querySelector(".la-panneau")) return;
        const bouton = document.querySelector(`#${derniereCarte} .la-acces [data-la-onglet="${dernierOnglet}"]`);
        if (bouton) bouton.focus();
      }, 0);
    },
    []
  );

  const surClavierOnglets = (evenement) => {
    if (evenement.key !== "ArrowRight" && evenement.key !== "ArrowLeft") return;
    const i = onglets.findIndex((o) => o.id === onglet);
    const prochain = onglets[(i + (evenement.key === "ArrowRight" ? 1 : -1) + onglets.length) % onglets.length];
    panneau.changerOnglet(prochain.id);
    evenement.preventDefault();
    const bouton = document.getElementById(`la-onglet-${prochain.id}`);
    if (bouton) bouton.focus();
  };

  return createPortal(
    <>
      <div className="la-voile" onClick={panneau.fermer} aria-hidden="true" />
      <aside className={`la la-panneau la-ton-${ton}`} role="dialog" aria-modal="false" aria-labelledby="la-panneau-titre">
        <div className="la-panneau__tete">
          <div className="la-panneau__barre">
            {produit}
            <div className="la-panneau__identite">
              <h2 id="la-panneau-titre" className="la-panneau__titre" tabIndex={-1} ref={refTitre}>
                {titre}
              </h2>
              {sousTitre && <p className="la-panneau__sous">{sousTitre}</p>}
            </div>
            <div className="la-panneau__nav">
              <button
                type="button"
                className="la-bouton-icone"
                disabled={!precedent}
                aria-label="Lead précédent (Alt + flèche haut)"
                title="Lead précédent (Alt + ↑)"
                onClick={() => panneau.naviguer(-1)}
              >
                <i className="fa fa-chevron-up" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="la-bouton-icone"
                disabled={!suivant}
                aria-label="Lead suivant (Alt + flèche bas)"
                title="Lead suivant (Alt + ↓)"
                onClick={() => panneau.naviguer(1)}
              >
                <i className="fa fa-chevron-down" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="la-bouton-icone"
                aria-label="Fermer le panneau (Échap)"
                title="Fermer (Échap)"
                onClick={panneau.fermer}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
          {etat && <div className="la-panneau__etat">{etat}</div>}
          <div className="la-onglets" role="tablist" aria-label="Sections du lead">
            {onglets.map((o) => (
              <button
                key={o.id}
                type="button"
                role="tab"
                id={`la-onglet-${o.id}`}
                className="la-onglet"
                aria-selected={onglet === o.id}
                aria-controls="la-panneau-contenu"
                tabIndex={onglet === o.id ? 0 : -1}
                title={o.libelle}
                onClick={() => panneau.changerOnglet(o.id)}
                onKeyDown={surClavierOnglets}
              >
                {o.court || o.libelle}
                {extra[o.id]}
              </button>
            ))}
          </div>
        </div>
        <div
          key={`${leadId}-${onglet}`}
          id="la-panneau-contenu"
          role="tabpanel"
          aria-labelledby={`la-onglet-${onglet}`}
          className={`la-panneau__contenu la-panneau__contenu--${onglet}`}
        >
          {contenu(onglet)}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default PanneauLead;
